/* eslint-disable camelcase */
import { useState } from 'react'

import { type CohortPath, type MeetingSplitDimension } from './exploration'
import { Trend } from './MeasurementPanels'
import { dimensionLabels, dimensionValueLabel, formatCount, percentage } from './model'
import { ErrorState, LoadingState, StaleNotice } from './shared'
import { isMissingEndpoint } from './useAttributionObservability'
import { useSummaryChipCohorts, useSummaryChipOverview, useSummaryChipTrend } from './useSummaryChipMetrics'

import styles from './MetricExplorer.module.scss'

import type { AttributionDimension, AttributionFilters, AttributionOverview } from '@/api/attributionObservability'
import type { SummaryChipCounts, SummaryChipOverview } from '@/api/summaryChipMetrics'

export const summaryChipValue = (summary?: SummaryChipCounts) => {
  if (!summary || !summary.measured_meetings) return 'Not measured'
  if (!summary.speaker_chip_count) return 'No speaker chips'
  return `${percentage(summary.named_speaker_chip_count, summary.speaker_chip_count)?.toFixed(1)}%`
}

export const toChipTrendSummary = (summary: SummaryChipCounts): AttributionOverview['trend'][number]['summary'] => ({
  meeting_count: summary.meeting_count,
  speaker_chip_count: summary.speaker_chip_count,
  named_speaker_chip_count: summary.named_speaker_chip_count,
  machine_labeled_speakers: 0,
  active_speakers: 0,
  ax_capture_ms: 0,
  ax_readable_ms: 0,
  speech_ms: 0,
  ax_covered_speech_ms: 0,
  words: 0,
  machine_labeled_words: 0,
})

function Unsupported({ dimensions, onReset }: { dimensions: string[]; onReset: () => void }) {
  return (
    <section className={styles.empty} role="status">
      <h2>This metric is unavailable for this view</h2>
      <p>
        Remove unsupported filters or splits:{' '}
        {dimensions.map((item) => dimensionLabels[item as AttributionDimension] ?? item).join(', ')}.
      </p>
      <button type="button" className={styles.splitButton} onClick={onReset}>
        Use supported filters and splits
      </button>
    </section>
  )
}

function ChipCohorts({
  filters,
  axes,
  path = [],
  onFilter,
}: {
  filters: AttributionFilters
  axes: MeetingSplitDimension[]
  path?: CohortPath
  onFilter: (path: CohortPath) => void
}) {
  const dimension = axes[path.length]
  const query = useSummaryChipCohorts(filters, dimension)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  if (query.isPending) return <LoadingState label="Loading speaker-chip breakdown…" />
  if (!query.data) return <ErrorState onRetry={() => void query.refetch()} />
  if (query.data.status !== 'available') return <p>Breakdown unavailable for this split.</p>
  return (
    <div className={styles.chipCohorts}>
      {query.isError && <StaleNotice onRetry={() => void query.refetch()} />}
      {query.data.groups.map(({ value, summary }) => {
        const nextPath: CohortPath = [...path, [dimension, value]]
        const hasChildren = path.length + 1 < axes.length
        const isExpanded = expanded.has(value)
        return (
          <div key={value}>
            <div className={styles.chipCohortRow}>
              {hasChildren && (
                <button
                  type="button"
                  className={styles.splitButton}
                  aria-expanded={isExpanded}
                  aria-label={`Expand ${dimensionValueLabel(dimension, value)}`}
                  onClick={() =>
                    setExpanded((current) => {
                      const next = new Set(current)
                      if (next.has(value)) next.delete(value)
                      else next.add(value)
                      return next
                    })
                  }
                >
                  {isExpanded ? '−' : '+'}
                </button>
              )}
              <button type="button" className={styles.splitButton} onClick={() => onFilter(nextPath)}>
                {dimensionValueLabel(dimension, value)}
              </button>
              <strong>{summaryChipValue(summary)}</strong>
              <span>
                {formatCount(summary.named_speaker_chip_count)} / {formatCount(summary.speaker_chip_count)} chips
              </span>
              <span>
                {formatCount(summary.measured_meetings)} / {formatCount(summary.meeting_count)} meetings measured
              </span>
            </div>
            {hasChildren && isExpanded && (
              <ChipCohorts
                filters={{
                  ...filters,
                  [dimension]: value,
                  negate: filters.negate?.filter((item) => item !== dimension),
                }}
                axes={axes}
                path={nextPath}
                onFilter={onFilter}
              />
            )}
          </div>
        )
      })}
      {query.data.truncated && <p>Showing the largest 100 groups. Filter to narrow this breakdown.</p>}
    </div>
  )
}

export default function SummaryChipPanels({
  filters,
  axes,
  overview,
  onAxes,
  onFilter,
  onUseSupported,
}: {
  filters: AttributionFilters
  axes: MeetingSplitDimension[]
  overview: ReturnType<typeof useSummaryChipOverview>
  onAxes: (axes: MeetingSplitDimension[]) => void
  onFilter: (path: CohortPath) => void
  onUseSupported: (supported: AttributionDimension[]) => void
}) {
  const data = overview.data
  const unsupportedAxes = axes.filter(
    (axis) =>
      !data?.supported_dimensions.includes(axis) ||
      (axis === 'participant_bucket' && filters.participant_source === 'invited')
  )
  const isSupported = data?.status === 'available' && !unsupportedAxes.length
  const grouped = useSummaryChipTrend(filters, axes, !!isSupported && axes.length > 0)
  if (overview.isPending) return <LoadingState label="Loading summary speaker chips…" skeleton />
  if (!data) {
    if (isMissingEndpoint(overview.error))
      return <p role="status">Not measured. Summary speaker chips require the updated backend.</p>
    return <ErrorState onRetry={() => void overview.refetch()} />
  }
  if (!isSupported)
    return (
      <Unsupported
        dimensions={[...data.unsupported_dimensions, ...unsupportedAxes]}
        onReset={() => onUseSupported(data.supported_dimensions)}
      />
    )
  const supported = data.supported_dimensions.filter(
    (dimension) =>
      (dimension !== 'participant_bucket' || filters.participant_source !== 'invited') &&
      (!filters[dimension] || filters.negate?.includes(dimension))
  )
  const trend = data.trend.map((point) => ({ ...point, summary: toChipTrendSummary(point.summary) }))
  const groups = grouped.data?.groups.map((group) => ({
    ...group,
    trend: group.trend.map((point) => ({ ...point, summary: toChipTrendSummary(point.summary) })),
  }))
  return (
    <section aria-label="Summary speaker chip results">
      <h2>Summary speaker chips named</h2>
      {overview.isError && <StaleNotice onRetry={() => void overview.refetch()} />}
      <SummaryChipDetail data={data} />
      <div className={styles.chipSplits}>
        {[...axes, ...(axes.length < supported.length ? [null] : [])].map((axis, index) => (
          <label key={index}>
            Split level {index + 1}
            <select
              aria-label={`Speaker-chip split level ${index + 1}`}
              value={axis ?? ''}
              onChange={(event) =>
                onAxes([
                  ...axes.slice(0, index),
                  ...(event.target.value ? [event.target.value as MeetingSplitDimension] : []),
                ])
              }
            >
              <option value="">No split</option>
              {supported
                .filter((item) => !axes.slice(0, index).includes(item))
                .map((item) => (
                  <option key={item} value={item}>
                    {dimensionLabels[item]}
                  </option>
                ))}
            </select>
          </label>
        ))}
      </div>
      {axes.length > 0 && (
        <ChipCohorts key={JSON.stringify([filters, axes])} filters={filters} axes={axes} onFilter={onFilter} />
      )}
      {axes.length === 0 && (
        <Trend metric="summary_speaker_chips_named" trend={trend} startAt={filters.start_at} endAt={filters.end_at} />
      )}
      {axes.length > 0 && grouped.isPending && <LoadingState label="Loading daily speaker-chip breakdown…" />}
      {axes.length > 0 && grouped.isError && !grouped.data && <ErrorState onRetry={() => void grouped.refetch()} />}
      {axes.length > 0 && grouped.isError && grouped.data && <StaleNotice onRetry={() => void grouped.refetch()} />}
      {axes.length > 0 && grouped.data?.status === 'unsupported' && (
        <Unsupported dimensions={grouped.data.unsupported_dimensions} onReset={() => onAxes([])} />
      )}
      {axes.length > 0 && grouped.data?.status === 'available' && (
        <Trend
          metric="summary_speaker_chips_named"
          trend={[]}
          groups={groups}
          truncated={grouped.data.truncated}
          startAt={filters.start_at}
          endAt={filters.end_at}
        />
      )}
    </section>
  )
}

export function SummaryChipDetail({ data }: { data: SummaryChipOverview }) {
  const summary = data.summary
  return (
    <p className={styles.measurementNote}>
      {formatCount(summary.named_speaker_chip_count)} of {formatCount(summary.speaker_chip_count)} summary speaker chips
      named · {formatCount(summary.measured_meetings)} / {formatCount(summary.meeting_count)} published meetings
      measured · {formatCount(summary.zero_chip_meetings)} without speaker chips ·{' '}
      {formatCount(summary.unmeasured_meetings)} unmeasured.
      <br />
      At summary publication; actual meeting speakers only. Individual accounts with data-sharing consent; enterprise
      accounts excluded. This population can differ from the collected meetings used by the other metrics.
    </p>
  )
}
